import { useCallback, useState } from "react";
import {
  patchOtpPreference,
  useUpdateUserSettingsMutation,
} from "@/features/user-settings/infrastructure/api/userSettingsApi";
import { SessionManager } from "../../infrastructure/session/SessionManager";
import {
  OTP_PREFERENCE_TOTP,
  PENDING_OTP_PREFERENCE_KEY,
  type OtpPreference,
} from "../../domain/constants/otpConstants";

const getApiErrorMessage = (err: unknown): string => {
  const data =
    err && typeof err === "object" && "data" in err
      ? (err as { data?: { error?: { code?: string; message?: string } } }).data
      : err && typeof err === "object" && "response" in err
        ? (err as {
            response?: { data?: { error?: { code?: string; message?: string } } };
          }).response?.data
        : undefined;

  if (data?.error?.code === "TOTP_NOT_ENABLED") {
    return "Set up your authenticator app before selecting it as your primary method.";
  }

  return data?.error?.message ?? "Unable to update OTP preference.";
};

export const useOtpPreference = () => {
  const [updateSettings, { isLoading: saving }] =
    useUpdateUserSettingsMutation();
  const [patching, setPatching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setOtpPreference = useCallback(
    async (
      preference: OtpPreference,
      isTotpEnabled: boolean,
    ): Promise<{ ok: true } | { ok: false; needsTotpSetup: true } | { ok: false; message: string }> => {
      setError(null);

      if (preference === OTP_PREFERENCE_TOTP && !isTotpEnabled) {
        sessionStorage.setItem(PENDING_OTP_PREFERENCE_KEY, OTP_PREFERENCE_TOTP);
        return { ok: false, needsTotpSetup: true };
      }

      const session = new SessionManager();
      const accessToken = session.getAccessToken();
      const fallbackToken = accessToken || session.getTempAccessToken();

      try {
        if (!fallbackToken) {
          throw new Error("No access token available");
        }

        if (accessToken) {
          await updateSettings({ otp_preference: preference }).unwrap();
        } else {
          setPatching(true);
          await patchOtpPreference(fallbackToken, preference);
        }

        return { ok: true };
      } catch (err: unknown) {
        const message = getApiErrorMessage(err);
        setError(message);
        return { ok: false, message };
      } finally {
        setPatching(false);
      }
    },
    [updateSettings],
  );

  return {
    setOtpPreference,
    loading: patching || saving,
    error,
    setError,
  };
};
